import { create } from 'zustand';
import type { Point2D } from '@shared/models/geometry.model';
import {
  DEFAULT_BUILDING_FOOTPRINT_TEMPLATE,
  type BuildingFootprintTemplateId,
} from '../helpers/building-footprint-template.helpers';
import type { Building } from '@shared/models/design/building-design.model';
import type { ViewMode } from '../components/ViewModeToggle';
import type { BlueprintInteractionMode } from '../blueprint-underlay/blueprint-underlay-controller';
import type { BlueprintOpacityPreview } from '../helpers/blueprint-opacity-preview.helpers';

/** Full Mapbox camera snapshot for Design tab session restore. */
export type DesignMapCameraState = {
  /** Map center as `[lng, lat]`. */
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
  /** Projection inset; an open side panel moves the visible camera center. */
  padding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  /** Three.js facade projection layered on top of Mapbox's capped 85° pitch. */
  facadeModeActive?: boolean;
  /** Progress from Mapbox's 85° projection to the exact 90° facade projection. */
  facadeTransitionProgress?: number;
  /** Screen-space vertical pan applied only to the exact facade projection. */
  facadeVerticalOffsetPixels?: number;
};

type DesignMapViewPreferences = {
  /** Last chosen Design map camera mode (toggle UI; kept in sync with pitch). */
  viewMode: ViewMode;
  /** Last Design map camera, including its panel-aware projection inset. */
  camera: DesignMapCameraState | undefined;
};

/** How Map3d should respond to a panel-driven PDF import request. */
export type BlueprintImportRequestKind = 'edit-pages' | 'add-pdf';

/**
 * Selection value meaning "no building is selected" (DEV-2576). Clicking the
 * map background clears the selection, which is different from `undefined`
 * (no explicit choice yet, which resolves to the first building).
 */
export const NO_BUILDING_SELECTED = '__no-building-selected__';

/**
 * True when {@link selectedBuildingId} points at a building (explicitly or via
 * the implicit first-building fallback).
 * @param selectedBuildingId Selected building id from the design store
 */
export const hasBuildingSelection = (selectedBuildingId?: string): boolean =>
  selectedBuildingId !== NO_BUILDING_SELECTED;

/**
 * Whether the user can currently see an active building or storey selection.
 *
 * An unset building id still resolves geometry writes to the first building,
 * but it is not a visible selection until a storey or building outline has
 * actually been selected.
 */
export const hasVisibleBuildingSelection = ({
  selectedBuildingId,
  selectedStoreyId,
  isBuildingOutlineSelected,
}: Pick<
  DesignStoreState,
  'selectedBuildingId' | 'selectedStoreyId' | 'isBuildingOutlineSelected'
>): boolean =>
  hasBuildingSelection(selectedBuildingId) &&
  (selectedBuildingId !== undefined ||
    selectedStoreyId !== undefined ||
    isBuildingOutlineSelected);

/**
 * Primary Design canvas tool (command bar).
 * - `build` — default: empty → draw; with geometry → edit points/lines
 * - `draw` — explicitly add another solid / cutout
 * - `moveBuilding` — translate all storeys (PDF stays put)
 * PDF selection is top chrome / object selection, not a tool.
 */
export type DesignMapTool = 'build' | 'draw' | 'moveBuilding';

export const DEFAULT_NEW_BUILDING_STOREY_COUNT = 5;

type DesignStoreState = DesignMapViewPreferences & {
  selectedStoreyId?: string;
  /**
   * Selected building id; undefined resolves to the first building and
   * {@link NO_BUILDING_SELECTED} means no building is selected (DEV-2576).
   */
  selectedBuildingId?: string;
  /**
   * Whole building selected (map footprint click) rather than one of its
   * storeys: the volume gets a blue outline and no storey is active.
   */
  isBuildingOutlineSelected: boolean;
  /**
   * True while one or more polygon vertices are selected on the map.
   * Backspace/Delete then removes vertices instead of the selected storey.
   */
  hasSelectedVertices: boolean;
  /**
   * True while a whole shape is selected on the map (no vertex required).
   * Backspace/Delete then removes that shape instead of the selected storey.
   */
  hasSelectedShape: boolean;
  /** Building under the pointer on the map; highlights its panel row. */
  hoveredBuildingId?: string;
  /** Unsaved building that becomes persistent when its first footprint is completed. */
  buildingCandidate?: Building;
  /** GFA entered before the candidate has a footprint; applied when it is completed. */
  buildingCandidateTargetGfa?: number;
  /** Number of storeys requested while creating the candidate. */
  buildingCandidateStoreyCount?: number;
  /** Footprint placement mode for the unfinished building candidate. */
  buildingCandidateFootprintTemplate: BuildingFootprintTemplateId;
  /**
   * Manual blueprint page override. Cleared when {@link selectedStoreyId} changes
   * so auto resolution by storey level resumes.
   */
  manualBlueprintUnderlayId?: string;
  /** Active Design canvas tool (Build / Draw / Move building). */
  designMapTool: DesignMapTool;
  /**
   * Blueprint underlay interaction: `transform` = PDF selected,
   * `calibrate` = measure scale on the map, `locked` = not selected.
   */
  blueprintInteractionMode: BlueprintInteractionMode;
  /** In-progress calibrate measure endpoints (local meters). */
  calibratePoints: Point2D[];
  /** Whether the calibration marking on the map should be emphasized. */
  blueprintCalibrationHighlight: boolean;
  /**
   * Opacity shown on the map while the Properties slider is dragged.
   * Cleared when the drag is saved.
   */
  blueprintOpacityPreview?: BlueprintOpacityPreview;
  /**
   * Incremented to ask Map3d to open a PDF underlay flow.
   * Kind selects page/storey dialog vs picking a new PDF file.
   */
  blueprintImportRequestId: number;
  blueprintImportRequestKind: BlueprintImportRequestKind;
  /** Incremented when a user action should animate the map to top-down. */
  quickTopDownRequestId: number;
  footprintCenterPlacementRequestId: number;
  setSelectedStoreyId: (selectedStoreyId?: string) => void;
  setSelectedBuildingId: (selectedBuildingId?: string) => void;
  setHoveredBuildingId: (hoveredBuildingId?: string) => void;
  selectBuildingOutline: (buildingId: string) => void;
  setHasSelectedVertices: (hasSelectedVertices: boolean) => void;
  setHasSelectedShape: (hasSelectedShape: boolean) => void;
  setBuildingCandidate: (buildingCandidate?: Building) => void;
  setBuildingCandidateTargetGfa: (targetGfa?: number) => void;
  setBuildingCandidateStoreyCount: (storeyCount: number) => void;
  setBuildingCandidateFootprintTemplate: (
    template: BuildingFootprintTemplateId,
  ) => void;
  setManualBlueprintUnderlayId: (manualBlueprintUnderlayId?: string) => void;
  setViewMode: (viewMode: ViewMode) => void;
  setCamera: (camera: DesignMapCameraState) => void;
  setDesignMapTool: (designMapTool: DesignMapTool) => void;
  setBlueprintInteractionMode: (mode: BlueprintInteractionMode) => void;
  setCalibratePoints: (points: Point2D[]) => void;
  setBlueprintCalibrationHighlight: (highlight: boolean) => void;
  setBlueprintOpacityPreview: (
    preview: BlueprintOpacityPreview | undefined,
  ) => void;
  requestBlueprintImport: (kind: BlueprintImportRequestKind) => void;
  requestQuickTopDown: () => void;
  requestFootprintCenterPlacement: () => void;
  /**
   * Bumped when the user selects a building or storey, so the right panel can
   * open Properties. Not tied to the selected id: re-selecting the current
   * target still counts.
   */
  propertiesPanelRequestId: number;
  requestPropertiesPanel: () => void;
  /** Right panel tab. Survives leaving and re-entering Design. */
  rightPanelTab: DesignRightPanelTab;
  setRightPanelTab: (rightPanelTab: DesignRightPanelTab) => void;
};

/** Agent, properties, or the comments thread in the Design right panel. */
export type DesignRightPanelTab = 'agent' | 'properties' | 'comments';

/** Defaults for ephemeral Design UI (not including actions). */
const DESIGN_SESSION_DEFAULTS = {
  selectedStoreyId: undefined as string | undefined,
  selectedBuildingId: undefined as string | undefined,
  hoveredBuildingId: undefined as string | undefined,
  isBuildingOutlineSelected: false,
  hasSelectedVertices: false,
  hasSelectedShape: false,
  buildingCandidate: undefined as Building | undefined,
  buildingCandidateTargetGfa: undefined as number | undefined,
  buildingCandidateStoreyCount: undefined as number | undefined,
  buildingCandidateFootprintTemplate: DEFAULT_BUILDING_FOOTPRINT_TEMPLATE,
  manualBlueprintUnderlayId: undefined as string | undefined,
  viewMode: '3d' as ViewMode,
  camera: undefined as DesignMapCameraState | undefined,
  designMapTool: 'build' as DesignMapTool,
  blueprintInteractionMode: 'locked' as BlueprintInteractionMode,
  calibratePoints: [] as Point2D[],
  blueprintCalibrationHighlight: false,
  blueprintOpacityPreview: undefined as BlueprintOpacityPreview | undefined,
  blueprintImportRequestId: 0,
  blueprintImportRequestKind: 'edit-pages' as BlueprintImportRequestKind,
  quickTopDownRequestId: 0,
  footprintCenterPlacementRequestId: 0,
  propertiesPanelRequestId: 0,
  rightPanelTab: 'properties' as DesignRightPanelTab,
};

/** Project id the design session is bound to (module-scoped across remounts). */
let designSessionProjectId: number | undefined;

/**
 * A Design mutation can rebase the internal location anchor while leaving the
 * visible geometry fixed. The next location sync must then update coordinates
 * without treating that bookkeeping change as a request to move the camera.
 */
let suppressNextLocationFlyTo = false;

export const suppressNextDesignMapLocationFlyTo = (): void => {
  suppressNextLocationFlyTo = true;
};

export const consumeDesignMapLocationFlyToSuppression = (): boolean => {
  const shouldSuppress = suppressNextLocationFlyTo;
  suppressNextLocationFlyTo = false;
  return shouldSuppress;
};

let discardActiveDraft: () => void = () => {};

/**
 * Register how the interactive Design map drops an in-progress footprint.
 * UI code calls {@link discardActiveDesignDraft} without importing the map.
 */
export const registerDesignDraftDiscard = (discard: () => void): void => {
  discardActiveDraft = discard;
};

/** Drop the in-progress footprint on the interactive Design map, if one is mounted. */
export const discardActiveDesignDraft = (): void => {
  discardActiveDraft();
};

/**
 * Ephemeral Design UI state for one project session. View preferences survive
 * Design ↔ Build tab switches; {@link DesignStoreState.buildingCandidate} is
 * explicitly discarded when Design unmounts.
 */
export const useDesignStore = create<DesignStoreState>((set) => {
  return {
    ...DESIGN_SESSION_DEFAULTS,
    setSelectedStoreyId: (selectedStoreyId?: string) => {
      set((state) => {
        // Picking a storey always leaves the whole-building selection.
        if (state.selectedStoreyId === selectedStoreyId) {
          return { selectedStoreyId, isBuildingOutlineSelected: false };
        }
        // Changing storey clears manual page override so auto level matching resumes.
        return {
          selectedStoreyId,
          manualBlueprintUnderlayId: undefined,
          isBuildingOutlineSelected: false,
        };
      });
    },
    setSelectedBuildingId: (selectedBuildingId?: string) => {
      set({ selectedBuildingId, isBuildingOutlineSelected: false });
    },
    setHoveredBuildingId: (hoveredBuildingId?: string) => {
      set({ hoveredBuildingId });
    },
    selectBuildingOutline: (buildingId: string) => {
      set({
        selectedBuildingId: buildingId,
        selectedStoreyId: undefined,
        isBuildingOutlineSelected: true,
      });
    },
    setHasSelectedVertices: (hasSelectedVertices: boolean) => {
      set({ hasSelectedVertices });
    },
    setHasSelectedShape: (hasSelectedShape: boolean) => {
      set({ hasSelectedShape });
    },
    setBuildingCandidate: (buildingCandidate?: Building) => {
      set((state) => ({
        buildingCandidate,
        ...(state.buildingCandidate?.id !== buildingCandidate?.id
          ? {
              buildingCandidateTargetGfa: undefined,
              buildingCandidateStoreyCount: buildingCandidate
                ? DEFAULT_NEW_BUILDING_STOREY_COUNT
                : undefined,
              buildingCandidateFootprintTemplate:
                DEFAULT_BUILDING_FOOTPRINT_TEMPLATE,
            }
          : {}),
      }));
    },
    setBuildingCandidateTargetGfa: (buildingCandidateTargetGfa?: number) =>
      set({ buildingCandidateTargetGfa }),
    setBuildingCandidateStoreyCount: (buildingCandidateStoreyCount: number) =>
      set({ buildingCandidateStoreyCount }),
    setBuildingCandidateFootprintTemplate: (
      buildingCandidateFootprintTemplate,
    ) => set({ buildingCandidateFootprintTemplate }),
    setManualBlueprintUnderlayId: (manualBlueprintUnderlayId?: string) =>
      set({ manualBlueprintUnderlayId }),
    setViewMode: (viewMode) => set({ viewMode }),
    setCamera: (camera) => set({ camera }),
    setDesignMapTool: (designMapTool) => {
      set(() => {
        // Leaving Build clears PDF selection — PDF chrome only while selected.
        if (designMapTool === 'build') return { designMapTool };
        return {
          designMapTool,
          blueprintInteractionMode: 'locked',
          calibratePoints: [],
        };
      });
    },
    setBlueprintInteractionMode: (blueprintInteractionMode) =>
      set({ blueprintInteractionMode }),
    setCalibratePoints: (calibratePoints) => set({ calibratePoints }),
    setBlueprintCalibrationHighlight: (blueprintCalibrationHighlight) =>
      set({ blueprintCalibrationHighlight }),
    setBlueprintOpacityPreview: (blueprintOpacityPreview) =>
      set((state) => {
        const current = state.blueprintOpacityPreview;
        if (
          current?.underlayId === blueprintOpacityPreview?.underlayId &&
          current?.opacity === blueprintOpacityPreview?.opacity
        ) {
          return state;
        }
        return { blueprintOpacityPreview };
      }),
    requestBlueprintImport: (kind) =>
      set((state) => ({
        blueprintImportRequestId: state.blueprintImportRequestId + 1,
        blueprintImportRequestKind: kind,
      })),
    requestFootprintCenterPlacement: () =>
      set((state) => ({
        footprintCenterPlacementRequestId:
          state.footprintCenterPlacementRequestId + 1,
      })),
    requestQuickTopDown: () =>
      set((state) => ({
        quickTopDownRequestId: state.quickTopDownRequestId + 1,
      })),
    requestPropertiesPanel: () =>
      set((state) => ({
        propertiesPanelRequestId: state.propertiesPanelRequestId + 1,
      })),
    setRightPanelTab: (rightPanelTab) => set({ rightPanelTab }),
  };
});

/**
 * Bind ephemeral Design UI to {@link projectId}.
 * Resets session state when opening a different project so stale import
 * requests (file picker) / camera / tool mode do not leak across projects.
 */
export const ensureDesignSessionForProject = (projectId: number): void => {
  if (designSessionProjectId === projectId) return;
  designSessionProjectId = projectId;
  suppressNextLocationFlyTo = false;
  useDesignStore.setState({ ...DESIGN_SESSION_DEFAULTS });
};

/**
 * Project the current ephemeral Design session belongs to, or `undefined`
 * before {@link ensureDesignSessionForProject} has run.
 */
export const getDesignSessionProjectId = (): number | undefined =>
  designSessionProjectId;
